import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Eye, Bookmark, MousePointer, Users, Sparkles, Lock, MoreHorizontal, Trash2, RotateCcw } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import NewCampaignDialog from '@/components/NewCampaignDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { DashboardEvent, OrgTotals } from '@/types'

const FREE_EVENT_LIMIT = 3

type DashboardEventsProps = {
  events: DashboardEvent[]
  totals: OrgTotals
  event_count: number
  archived_count: number
  event_limit_reached: boolean
  pro: boolean
  status_filter: 'active' | 'archived'
}

function statusBadge(status: DashboardEvent['status']) {
  if (status === 'published') return { label: 'Published', variant: 'default' as const }
  if (status === 'archived') return { label: 'Archived', variant: 'outline' as const }
  return { label: 'Draft', variant: 'secondary' as const }
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return 'No event date'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

type TotalCardProps = {
  label: string
  value: number
  icon: ReactNode
}

function TotalCard({ label, value, icon }: TotalCardProps) {
  return (
    <div className="rounded-xl border border-black/10 bg-card p-4 shadow-xs">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-base font-medium">{label}</span>
      </div>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value.toLocaleString()}</p>
    </div>
  )
}

function RowActionsMenu({ event }: { event: DashboardEvent }) {
  const [confirming, setConfirming] = useState(false)
  const timeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    }
  }, [])

  function handleDeleteSelect(e: Event) {
    if (!confirming) {
      e.preventDefault()
      setConfirming(true)
      timeoutRef.current = window.setTimeout(() => setConfirming(false), 3000)
      return
    }
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    router.delete(`/dashboard/events/${event.id}`, { preserveScroll: true })
  }

  const showCheckIn = event.status === 'published' && event.page_kind === 'event'

  if (event.status === 'archived') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8">
            <MoreHorizontal className="size-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[160px]">
          <DropdownMenuItem
            onSelect={() => router.patch(`/dashboard/events/${event.id}/restore`, {}, { preserveScroll: true })}
          >
            <RotateCcw className="mr-2 size-3.5" />
            Restore
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <DropdownMenu onOpenChange={(open) => { if (!open) setConfirming(false) }}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-8">
          <MoreHorizontal className="size-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        <DropdownMenuItem asChild>
          <Link href={`/dashboard/events/${event.id}`}>Overview</Link>
        </DropdownMenuItem>
        {showCheckIn && (
          <DropdownMenuItem asChild>
            <Link href={`/dashboard/events/${event.id}/check_ins`}>Check in</Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {event.locked ? (
          <DropdownMenuItem disabled>Edit</DropdownMenuItem>
        ) : (
          <DropdownMenuItem asChild>
            <Link href={`/dashboard/events/${event.id}/edit`}>Edit</Link>
          </DropdownMenuItem>
        )}
        {!event.locked && (
          <DropdownMenuItem
            onSelect={handleDeleteSelect}
            className={confirming ? 'text-destructive focus:text-destructive' : ''}
          >
            {confirming ? (
              <>
                <Trash2 className="mr-2 size-3.5" />
                Click to confirm
              </>
            ) : (
              'Delete'
            )}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default function DashboardEventsIndex({ events, totals, event_count, archived_count, event_limit_reached, pro, status_filter }: DashboardEventsProps) {
  const showTabs = archived_count > 0 || status_filter === 'archived'
  return (
    <>
      <Head title="Campaigns" />
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-3xl font-semibold tracking-tight">Campaigns</h1>
            {!pro && (
              <span className="text-base text-muted-foreground">
                {event_count} of {FREE_EVENT_LIMIT} free campaigns used
              </span>
            )}
          </div>
          {event_limit_reached ? (
            <Button asChild>
              <a href="/dashboard/billing">
                <Sparkles className="mr-1.5 size-4" />
                Upgrade for Unlimited Campaigns
              </a>
            </Button>
          ) : (
            <NewCampaignDialog trigger={<Button>New Campaign</Button>} />
          )}
        </div>

        {event_limit_reached && (
          <div className="mb-8 flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 px-5 py-4">
            <div>
              <p className="font-medium text-foreground">
                You've used all {FREE_EVENT_LIMIT} free campaigns
              </p>
              <p className="mt-0.5 text-base text-muted-foreground">
                Upgrade to Pro for unlimited campaigns, plus honorees, sponsors, and more.
              </p>
            </div>
            <Button asChild size="sm" className="ml-4 shrink-0">
              <a href="/dashboard/billing">Upgrade to Pro</a>
            </Button>
          </div>
        )}

        {/* Org-level totals */}
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <TotalCard label="Total Views" value={totals.total_views} icon={<Eye className="size-4" />} />
          <TotalCard label="Total Saves" value={totals.total_saves} icon={<Bookmark className="size-4" />} />
          <TotalCard label="Total Clicks" value={totals.total_clicks} icon={<MousePointer className="size-4" />} />
          <TotalCard label="Followers" value={totals.total_followers} icon={<Users className="size-4" />} />
        </div>

        {showTabs && (
          <div className="mb-4 inline-flex rounded-lg border border-black/10 bg-card p-0.5 shadow-xs">
            <FilterTab label="Active" count={event_count} href="/dashboard/events" current={status_filter === 'active'} />
            <FilterTab label="Archived" count={archived_count} href="/dashboard/events?status=archived" current={status_filter === 'archived'} />
          </div>
        )}

        {events.length === 0 ? (
          <div className="rounded-lg border border-dashed p-12 text-center">
            <p className="text-muted-foreground">
              {status_filter === 'archived'
                ? 'No archived campaigns.'
                : "You haven't created any campaigns yet."}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3 sm:hidden">
              {events.map((event) => (
                <div key={event.id} className="rounded-lg border border-black/10 bg-card p-4 shadow-xs">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {event.status === 'archived' ? (
                        <span className="block truncate font-medium">{event.title}</span>
                      ) : (
                        <Link
                          href={`/dashboard/events/${event.id}`}
                          className="block truncate font-medium underline-offset-4 hover:underline"
                        >
                          {event.title}
                        </Link>
                      )}
                      <p className="mt-1 text-sm text-muted-foreground">
                        {event.page_kind === 'fundraiser' ? 'Fundraising page' : 'Event page'} - {formatDate(event.date)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Badge variant={statusBadge(event.status).variant}>
                        {statusBadge(event.status).label}
                      </Badge>
                      {event.visibility === 'private' && (
                        <Badge variant="outline">Private</Badge>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                    <Metric label="Supporters" value={event.supporter_count.toLocaleString()} />
                    <Metric label="Raised" value={formatMoney(event.raised_cents)} />
                    <Metric label="Created" value={formatDateTime(event.created_at)} />
                  </div>
                  <div className="mt-4 flex justify-end">
                    <RowActionsMenu event={event} />
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs sm:block">
              <Table className="min-w-[860px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Supporters</TableHead>
                  <TableHead className="text-right">Raised</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-medium">
                      {event.status === 'archived' ? (
                        event.title
                      ) : (
                        <Link
                          href={`/dashboard/events/${event.id}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {event.title}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{event.page_kind === 'fundraiser' ? 'Fundraising page' : 'Event page'}</span>
                        <span className="text-xs text-muted-foreground">{formatDate(event.date)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {event.supporter_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(event.raised_cents)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge variant={statusBadge(event.status).variant}>
                          {statusBadge(event.status).label}
                        </Badge>
                        {event.page_kind === 'fundraiser' && (
                          <Badge variant="outline">Fundraiser</Badge>
                        )}
                        {event.visibility === 'private' && (
                          <Badge variant="outline">Private</Badge>
                        )}
                        {event.locked && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            <Lock className="mr-1 size-3" />
                            Pending Verification
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatDateTime(event.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <RowActionsMenu event={event} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </>
  )
}

function FilterTab({ label, count, href, current }: { label: string; count: number; href: string; current: boolean }) {
  return (
    <Link
      href={href}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
        current ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
      <span className="ml-1.5 tabular-nums opacity-70">{count}</span>
    </Link>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium tabular-nums">{value}</p>
    </div>
  )
}

DashboardEventsIndex.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
